import {useEffect} from "react";
import {useLogout} from "./useLogout";
import {
    getLastSessionActivity,
    isSessionIdleExpired,
    recordSessionActivity,
    SESSION_LAST_ACTIVITY_KEY,
} from "../utils/sessionTimeoutUtils";

const ACTIVITY_WRITE_THROTTLE_MS = 30 * 1000;
const SESSION_CHECK_INTERVAL_MS = 60 * 1000;

const ACTIVITY_EVENTS = ["pointerdown", "keydown", "wheel", "scroll", "touchstart"] as const;

export const useIdleLogout = () => {
    const {logout} = useLogout();

    useEffect(() => {
        let lastActivityWrite = getLastSessionActivity() ?? Date.now();
        let logoutStarted = false;

        const logoutIfExpired = (): boolean => {
            if (logoutStarted) {
                return true;
            }

            if (!isSessionIdleExpired()) {
                return false;
            }

            logoutStarted = true;
            void logout();

            return true;
        };

        const handleActivity = () => {
            // Critical: check expiry BEFORE recording new activity.
            // Otherwise the first click after 5 hours could revive
            // an already-expired session.
            if (logoutIfExpired()) {
                return;
            }

            const now = Date.now();

            // Avoid writing localStorage on every scroll/touch event.
            if (now - lastActivityWrite < ACTIVITY_WRITE_THROTTLE_MS) {
                return;
            }

            recordSessionActivity(now);
            lastActivityWrite = now;
        };

        const handleVisibilityChange = () => {
            if (document.visibilityState === "visible") {
                logoutIfExpired();
            }
        };

        const handleStorage = (event: StorageEvent) => {
            if (event.key !== SESSION_LAST_ACTIVITY_KEY) {
                return;
            }

            // Another tab logged the user out.
            if (event.newValue === null && !logoutStarted) {
                logoutStarted = true;
                void logout();
            }
        };

        // Defensive fallback. Normally authenticationService
        // initializes this before AuthContent renders.
        if (getLastSessionActivity() === undefined) {
            recordSessionActivity();
        } else if (logoutIfExpired()) {
            return;
        }

        ACTIVITY_EVENTS.forEach((event) => {
            window.addEventListener(event, handleActivity, {
                passive: true,
            });
        });

        document.addEventListener("visibilitychange", handleVisibilityChange);

        window.addEventListener("storage", handleStorage);

        const interval = window.setInterval(logoutIfExpired, SESSION_CHECK_INTERVAL_MS);

        return () => {
            ACTIVITY_EVENTS.forEach((event) => {
                window.removeEventListener(event, handleActivity);
            });

            document.removeEventListener("visibilitychange", handleVisibilityChange);

            window.removeEventListener("storage", handleStorage);

            window.clearInterval(interval);
        };
    }, [logout]);
};
